import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Shop, ShopInvitation } from '@reims-local/types';
import { adminApi } from '../lib/api';

export function ShopDetail() {
  const { id } = useParams<{ id: string }>();
  const [shop, setShop] = useState<Shop | null>(null);
  const [invitations, setInvitations] = useState<ShopInvitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const res = await adminApi.getShop(id);
      setShop(res.data.shop);
      setInvitations(res.data.invitations);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function sendInvitation() {
    if (!id) return;
    setSending(true);
    try {
      await adminApi.sendInvitation(id);
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  if (loading) return <p>Chargement…</p>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!shop) return <p>Introuvable.</p>;

  return (
    <>
      <Link to="/shops">← Retour</Link>
      <h2 style={{ marginTop: 12 }}>{shop.name}</h2>

      <div className="card" style={{ marginBottom: 24 }}>
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 24px', margin: 0 }}>
          <dt className="muted">Slug</dt><dd>{shop.slug}</dd>
          <dt className="muted">Type</dt><dd>{shop.shop_type}</dd>
          <dt className="muted">Statut</dt><dd>{shop.status}</dd>
          <dt className="muted">Plan</dt><dd>{shop.plan}</dd>
          <dt className="muted">Email</dt><dd>{shop.email ?? '—'}</dd>
          <dt className="muted">Téléphone</dt><dd>{shop.phone ?? '—'}</dd>
          <dt className="muted">Adresse</dt><dd>{shop.address ?? '—'}</dd>
          <dt className="muted">Partenaire</dt><dd>{shop.partner_included ? 'Vitrines de Reims' : '—'}</dd>
          <dt className="muted">Stripe</dt>
          <dd>
            {shop.stripe_account_id ? `${shop.stripe_account_id} (charges: ${shop.charges_enabled ? 'oui' : 'non'})` : '—'}
          </dd>
        </dl>
      </div>

      <div className="toolbar">
        <h3 style={{ margin: 0 }}>Invitations</h3>
        <div className="spacer" />
        <button className="primary" onClick={sendInvitation} disabled={sending || !shop.email}>
          {sending ? 'Envoi…' : 'Envoyer une invitation'}
        </button>
      </div>

      {invitations.length === 0 ? (
        <p className="muted">Aucune invitation envoyée.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Statut</th>
              <th>Envoyée</th>
              <th>Ouverte</th>
              <th>Activée</th>
              <th>Expire</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.email}</td>
                <td>
                  <span className={`badge ${invitationClass(inv.status)}`}>{inv.status}</span>
                </td>
                <td className="muted">{formatDate(inv.created_at)}</td>
                <td className="muted">{inv.opened_at ? formatDate(inv.opened_at) : '—'}</td>
                <td className="muted">{inv.activated_at ? formatDate(inv.activated_at) : '—'}</td>
                <td className="muted">{formatDate(inv.expires_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function invitationClass(status: string): string {
  if (status === 'activated') return 'green';
  if (status === 'opened') return 'blue';
  if (status === 'expired') return 'red';
  return 'orange';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
